import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError, workspaceApi } from '../../lib/api';
import { useSession } from '../../app/session';
import { Alert, Button, CARD_FRAME, Field, Input } from '../../components/ui';
import { BrandGlyph, BrandWordmark } from '../../components/ui/brand';
import i18n from '../../i18n';

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : i18n.t('auth.genericError');
}

/** The path to a first automation (R39): naming the workspace is step one of three. */
const STEPS = ['name', 'channel', 'automation'] as const;

/**
 * Name your workspace (R39, `R39-Onboarding.dc.html`): a three-step progress
 * that says the path is short, an "ex. Acme Marketing" placeholder, and the
 * reason under the button while no name is typed.
 */
export function CreateWorkspacePage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const { refresh } = useSession();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => workspaceApi.create(name),
    onSuccess: async () => {
      await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: ['workspaces'] })]);
      navigate('/', { replace: true });
    },
  });

  return (
    <main className="flex min-h-dvh flex-col items-center bg-bg px-4 py-14">
      <div className="flex w-full max-w-[520px] flex-col gap-7">
        {/* A lockup, not an uppercase eyebrow: the charte forbids "REPLIQ" (§01). */}
        <span className="flex items-center gap-2.5">
          <BrandGlyph size={24} surface="var(--color-bg)" />
          <BrandWordmark className="text-lg text-ink" />
        </span>
        {/* Where this leads (A15-01). TODO(refonte): steps 2 and 3 are the Channels page and the first automation; nothing tracks them yet, so only step 1 is ever current here. */}
        <ol aria-label={t('workspace.steps')} className="grid grid-cols-3 gap-2">
          {STEPS.map((step, index) => {
            const current = index === 0;
            return (
              <li key={step} aria-current={current ? 'step' : undefined} className="flex flex-col gap-1.5">
                <span aria-hidden className={`h-1 rounded-full ${current ? 'bg-primary-600' : 'bg-surface-3'}`} />
                <span className={`text-ds-xs ${current ? 'font-semibold text-primary-700' : 'text-ink-3'}`}>{t(`workspace.step.${step}`)}</span>
              </li>
            );
          })}
        </ol>
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-[28px] leading-[34px] tracking-[-0.01em] text-ink sm:text-[2.5rem] sm:leading-[1.08]">{t('workspace.title')}</h1>
          <p className="text-base leading-6 text-ink-2">{t('workspace.lede')}</p>
        </div>

        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className={`${CARD_FRAME} flex flex-col gap-4 p-5`}
        >
          {mutation.isError ? <Alert tone="err">{errorMessage(mutation.error)}</Alert> : null}
          <Field label={t('workspace.name')} hint={t('workspace.nameHint')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus placeholder={t('workspace.namePlaceholder')} className="h-11" />
          </Field>
          {/* Always a reason string, so the button is never re-mounted as the name is typed (A00-02). */}
          <Button
            type="submit"
            size="lg"
            loading={mutation.isPending}
            disabled={mutation.isPending || name.trim().length === 0}
            disabledReason={name.trim().length === 0 ? t('workspace.nameNeeded') : ''}
          >
            {mutation.isPending ? t('workspace.creating') : t('workspace.create')}
          </Button>
        </form>
      </div>
    </main>
  );
}
